import { avatarSrc } from "@shared/avatars.ts";
import { formatUnreadBadge } from "@shared/push-shared.ts";
import { type ReactNode, useState } from "react";
import { FaCrown } from "react-icons/fa6";

type Size = "sm" | "md" | "lg" | "xl";

type Props = {
  name?: string | null;
  image?: string | null;
  updatedAt?: Date | string | number | null;
  size?: Size;
  role?: string | null;
  decorative?: boolean;
  className?: string;
};

const BOX = {
  sm: "h-8 w-8 text-xs",
  md: "h-9 w-9 text-sm",
  lg: "h-11 w-11 text-sm",
  xl: "h-16 w-16 text-xl",
} as const;

const CROWN = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-4 w-4",
  xl: "h-5 w-5",
} as const;

function withCrown(face: ReactNode, role: string | null | undefined, size: Size) {
  const kind = role === "admin" || role === "staff" ? role : null;
  if (!kind) return face;
  return (
    <span
      className="relative inline-flex shrink-0"
      title={kind === "admin" ? "Superuser" : "Admin"}
    >
      {face}
      <FaCrown
        aria-hidden
        className={`pointer-events-none absolute -left-1 -top-1.5 drop-shadow ${CROWN[size]} ${
          kind === "admin" ? "text-amber-400" : "text-slate-400"
        }`}
      />
    </span>
  );
}

export function UserAvatar({
  name,
  image,
  updatedAt,
  size = "sm",
  role,
  decorative = false,
  className = "",
}: Props) {
  const label = name?.trim() || "Account";
  const src = avatarSrc(image, updatedAt);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const box = BOX[size];

  if (src && failedSrc !== src) {
    return withCrown(
      <img
        src={src}
        alt={decorative ? "" : `${label} avatar`}
        aria-hidden={decorative || undefined}
        referrerPolicy="no-referrer"
        onError={() => setFailedSrc(src)}
        className={`inline-block shrink-0 rounded-full object-cover ${box} ${className}`}
      />,
      role,
      size,
    );
  }

  const initials =
    label
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "A";

  const classNameFull = `inline-flex shrink-0 items-center justify-center rounded-full bg-primary-700 font-semibold text-white ${box} ${className}`;
  if (decorative) {
    return withCrown(
      <span aria-hidden className={classNameFull}>
        {initials}
      </span>,
      role,
      size,
    );
  }
  return withCrown(
    <span role="img" aria-label={`${label} avatar`} className={classNameFull}>
      {initials}
    </span>,
    role,
    size,
  );
}

export function UnreadBadge({ count, floating = true }: { count: number; floating?: boolean }) {
  const label = formatUnreadBadge(count);
  if (!label) return null;
  return (
    <span
      aria-hidden
      className={`${floating ? "absolute -top-1 -right-1" : ""} inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white`}
    >
      {label}
    </span>
  );
}
